import {useContext} from 'react'
import {useTranslation} from 'react-i18next'

import {useCreateMediaLibraryEntry, useGetMediaLibraryEntryByFile} from '@sdox/api'

import {MediaLibraryContext} from './MediaLibraryContext'
import {useCollectionId, useDocumentId} from '../../hooks'
import {FileUpload} from '../FileUpload/FileUpload'
import {MediaLibraryFileType} from './types'

import type {XcMediaRefFragment} from '@sdox/api'

export const MAX_ALLOWED_FILE_SIZE = 50 * 1024 * 1024

interface MediaUploadProps {
  onUpload?: (mediaRef: XcMediaRefFragment) => void
}

const mimeTypeMap: Record<MediaLibraryFileType, string[]> = {
  [MediaLibraryFileType.Image]: ['image/*'],
  [MediaLibraryFileType.Video]: ['video/mp4'],
  [MediaLibraryFileType.PDF]: ['application/pdf'],
}

const getMimeTypesFromFileTypes = (fileTypes: MediaLibraryFileType[]): string[] => {
  return fileTypes.flatMap(fileType => mimeTypeMap[fileType] || [])
}

const isFileTypeAccepted = (acceptedFileTypes: MediaLibraryFileType[], fileType: string): boolean => {
  const isImage = fileType.startsWith('image')
  if (isImage) {
    return acceptedFileTypes.includes(MediaLibraryFileType.Image)
  }

  const isVideo = fileType === 'video/mp4'
  if (isVideo) {
    return acceptedFileTypes.includes(MediaLibraryFileType.Video)
  }

  const isPdf = fileType === 'application/pdf'
  if (isPdf) {
    return acceptedFileTypes.includes(MediaLibraryFileType.PDF)
  }

  return false
}

export const MediaUpload = (props: MediaUploadProps) => {
  const {onUpload} = props
  const documentId = useDocumentId()
  const collectionId = useCollectionId()
  const {acceptedFileTypes} = useContext(MediaLibraryContext)
  const createEntry = useCreateMediaLibraryEntry({documentId, collectionId})
  const getMediaLibraryEntryByFile = useGetMediaLibraryEntryByFile(documentId)
  const {t} = useTranslation()

  const accept = getMimeTypesFromFileTypes(acceptedFileTypes).join(',')

  const uploadFile = async (file: File): Promise<void> => {
    const {data} = await createEntry(file)
    if (data) {
      onUpload?.(data.createDocumentXcMediaLibraryEntry)
    }
  }

  const validate = async (file: File): Promise<true | {msg: string}> => {
    if (file.size > MAX_ALLOWED_FILE_SIZE) {
      return {msg: t('cufe:upload.fileSizeError')}
    }

    if (!isFileTypeAccepted(acceptedFileTypes, file.type)) {
      return {msg: t('cufe:upload.invalidFileTypeError')}
    }
    const mediaRef = await getMediaLibraryEntryByFile(file)
    if (mediaRef) {
      onUpload?.(mediaRef)
      return {msg: t('cufe:upload.fileAlreadyExistsError')}
    }

    return true
  }

  return (
    <form>
      <div className={'flex'}>
        <div className={'grow'}>
          <FileUpload
            accept={accept}
            onUpload={uploadFile}
            validate={validate}
          />
        </div>
      </div>
    </form>
  )
}
